import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { X, ChevronRight, MousePointerClick } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

export interface SpotlightStep {
  /** data-tour attribute value on the target element */
  target: string;
  /** Route the user should navigate to (sidebar link href) */
  route?: string;
  title: string;
  description: string;
  /** If true, the user must click the highlighted element to advance */
  clickToAdvance?: boolean;
}

interface SpotlightTourProps {
  steps: SpotlightStep[];
  currentStep: number;
  active: boolean;
  onAdvance: () => void;
  onSkip: () => void;
  onComplete: () => void;
}

export function SpotlightTour({
  steps,
  currentStep,
  active,
  onAdvance,
  onSkip,
  onComplete,
}: SpotlightTourProps) {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipSide, setTooltipSide] = useState<"right" | "bottom">("right");
  const navigate = useNavigate();
  const location = useLocation();

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;

  // Find and measure the target element
  const measureTarget = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);

      // Decide tooltip placement
      if (rect.right + 320 < window.innerWidth) {
        setTooltipSide("right");
      } else {
        setTooltipSide("bottom");
      }
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (!active || !step) return;
    // Small delay to let DOM settle after navigation
    const timer = setTimeout(measureTarget, 150);
    window.addEventListener("resize", measureTarget);
    window.addEventListener("scroll", measureTarget, true);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", measureTarget);
      window.removeEventListener("scroll", measureTarget, true);
    };
  }, [active, step, measureTarget, location.pathname]);

  // Listen for clicks on the target element when clickToAdvance is true
  useEffect(() => {
    if (!active || !step?.clickToAdvance) return;

    const handler = (e: MouseEvent) => {
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el && (el === e.target || el.contains(e.target as Node))) {
        // Let the navigation happen, then advance
        setTimeout(() => {
          if (isLast) {
            onComplete();
          } else {
            onAdvance();
          }
        }, 300);
      }
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [active, step, isLast, onAdvance, onComplete]);

  if (!active || !step) return null;

  // If no target found yet, show a loading state
  if (!targetRect) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
        <div className="bg-card rounded-xl p-6 shadow-2xl max-w-sm text-center space-y-3">
          <p className="text-foreground font-medium">Finding element…</p>
          <Button variant="ghost" size="sm" onClick={onSkip}>Skip tour</Button>
        </div>
      </div>,
      document.body
    );
  }

  const padding = 6;
  const spotX = targetRect.left - padding;
  const spotY = targetRect.top - padding;
  const spotW = targetRect.width + padding * 2;
  const spotH = targetRect.height + padding * 2;

  // Tooltip position
  let tooltipStyle: React.CSSProperties;
  if (tooltipSide === "right") {
    tooltipStyle = {
      top: spotY,
      left: spotX + spotW + 16,
      maxWidth: 320,
    };
  } else {
    tooltipStyle = {
      top: spotY + spotH + 16,
      left: Math.max(16, spotX),
      maxWidth: 320,
    };
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ pointerEvents: "none" }} aria-modal="true">
      {/* Overlay with spotlight cutout — uses 4 rects around the target instead of SVG mask */}
      {/* Top */}
      <div className="absolute bg-black/60" style={{ pointerEvents: "auto", top: 0, left: 0, right: 0, height: spotY }} />
      {/* Bottom */}
      <div className="absolute bg-black/60" style={{ pointerEvents: "auto", top: spotY + spotH, left: 0, right: 0, bottom: 0 }} />
      {/* Left */}
      <div className="absolute bg-black/60" style={{ pointerEvents: "auto", top: spotY, left: 0, width: spotX, height: spotH }} />
      {/* Right */}
      <div className="absolute bg-black/60" style={{ pointerEvents: "auto", top: spotY, left: spotX + spotW, right: 0, height: spotH }} />

      {/* Spotlight ring around target — pointer events pass through */}
      <div
        className="absolute rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-transparent animate-pulse"
        style={{
          top: spotY,
          left: spotX,
          width: spotW,
          height: spotH,
          pointerEvents: "none",
        }}
      />

      {/* Tooltip card */}
      <div
        className="absolute bg-card border border-border rounded-xl shadow-2xl p-5 space-y-3 animate-in fade-in-0 slide-in-from-left-2 duration-300"
        style={{ ...tooltipStyle, pointerEvents: "auto" }}
      >
        {/* Close button */}
        <button
          onClick={onSkip}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div>
          <h3 className="font-semibold text-foreground text-base">{step.title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between gap-3 pt-1">
          <span className="text-xs text-muted-foreground">
            {currentStep + 1} / {steps.length}
          </span>

          <div className="flex items-center gap-2">
            {step.clickToAdvance ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                <MousePointerClick className="h-3.5 w-3.5" />
                Click it to continue
              </span>
            ) : (
              <Button
                size="sm"
                onClick={() => {
                  if (isLast) {
                    onComplete();
                  } else {
                    onAdvance();
                  }
                }}
              >
                {isLast ? "Finish" : "Next"}
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-muted rounded-full h-1">
          <div
            className="bg-primary h-1 rounded-full transition-all duration-500"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
